import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { BookOpen, Download, FileText, X } from 'lucide-react';
import { SkeletonTableRows } from '../ui/kit';
import PageHeader from '../components/PageHeader';
import { alsDatum, datumUndZeit } from '../lib/zeit';

// Auftragsbuch (§ 49 Abs. 4 PBefG): Kette Auftragseingang → Annahme → Fahrtbeginn → Fahrtende
// je Fahrzeug/Zeitraum. Einträge schreibt das Backend automatisch bei Vermittlung/Annahme;
// hier nur lesen + exportieren (CSV/PDF). Aufbewahrung mind. 1 Jahr — kein Löschen möglich.

const STATUS_DE = {
  searching: 'In Vermittlung', scheduled: 'Reserviert', accepted: 'Angenommen',
  arrived: 'Angekommen', in_progress: 'Unterwegs', completed: 'Abgeschlossen',
  cancelled: 'Storniert', no_driver: 'Kein Fahrer',
};
const STATUS_COLOR = { completed: '#059669', cancelled: '#dc2626', in_progress: '#7c3aed' };

// SQLite-UTC („YYYY-MM-DD HH:MM:SS") bzw. ISO → Berliner Zeit „TT.MM.JJ, HH:MM" (lib/zeit).
// Ein nicht lesbarer Wert bleibt wie bisher roh sichtbar — das Auftragsbuch verschluckt nichts.
const fmtTs = (ts) => (ts && !alsDatum(ts) ? String(ts) : datumUndZeit(ts));

function defaultRange() {
  const to = new Date();
  const from = new Date(); from.setDate(to.getDate() - 30);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
}

export default function OrderLog() {
  const location = useLocation();
  const [entries, setEntries] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState('');
  const [range, setRange] = useState(defaultRange());
  // ⌘K-Palette: /order-log?search=<q> übernimmt den Begriff und reicht ihn an den bestehenden
  // Lade-Call als `search` weiter. Ohne Parameter verhält sich die Seite exakt wie bisher.
  const [search, setSearch] = useState(() => (new URLSearchParams(location.search).get('search') || '').trim());
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState('');

  // Spätere ⌘K-Navigationen (auch von dieser Seite aus) übernehmen den Begriff ebenfalls;
  // der Mount ist über den Startwert oben abgedeckt.
  const skipFirstLocationRef = useRef(true);
  useEffect(() => {
    if (skipFirstLocationRef.current) { skipFirstLocationRef.current = false; return; }
    const q = new URLSearchParams(location.search).get('search');
    if (q !== null) setSearch(q.trim());
  }, [location]);

  useEffect(() => {
    api.get('/partners/vehicles').then(r => setVehicles(r.data.vehicles || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/partners/order-log', { params: { from: range.from, to: range.to, vehicle_id: vehicleId || undefined, search: search || undefined } })
      .then(r => setEntries(r.data.entries || []))
      .catch(() => toast.error('Auftragsbuch konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, [range, vehicleId, search]);

  useEffect(() => { load(); }, [load]);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const r = await api.get('/partners/order-log', {
        // Export spiegelt exakt die sichtbare Liste — inklusive eines aktiven Suchbegriffs.
        params: { from: range.from, to: range.to, vehicle_id: vehicleId || undefined, search: search || undefined, format },
        responseType: 'blob',
      });
      const type = format === 'pdf' ? 'application/pdf' : 'text/csv;charset=utf-8';
      const url = window.URL.createObjectURL(new Blob([r.data], { type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Auftragsbuch_${range.from}_${range.to}.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally {
      setExporting('');
    }
  };

  const cols = ['Auftragseingang', 'Annahme', 'Fahrtbeginn', 'Fahrtende', 'Abholung → Ziel', 'Fahrer', 'Fahrzeug', 'Status'];

  return (
    <div>
      <PageHeader
        title="Auftragsbuch"
        actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => exportFile('csv')} disabled={!!exporting}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: '#f3f4f6', borderRadius: 20, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', opacity: exporting ? 0.6 : 1 }}>
            <Download size={15} /> {exporting === 'csv' ? 'Erstelle …' : 'CSV'}
          </button>
          <button onClick={() => exportFile('pdf')} disabled={!!exporting}
            style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 16px', background: '#000', color: '#fff', borderRadius: 20, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer', opacity: exporting ? 0.6 : 1 }}>
            <FileText size={15} /> {exporting === 'pdf' ? 'Erstelle …' : 'PDF'}
          </button>
        </div>
        }
      />
      <div style={{ fontSize: 13, color: '#4b5563', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 6 }}>
        <BookOpen size={14} /> Dokumentation des Auftragseingangs am Betriebssitz (§ 49 Abs. 4 PBefG) · Aufbewahrung mindestens 1 Jahr
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Fahrzeug</div>
          <select value={vehicleId} onChange={e => setVehicleId(e.target.value)}
            style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13, minWidth: 200 }}>
            <option value="">Alle Fahrzeuge</option>
            {vehicles.map(v => (
              <option key={v.id} value={v.id}>{[v.brand, v.model].filter(Boolean).join(' ')} · {v.license_plate || 'ohne Kennzeichen'}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Startdatum</div>
          <input type="date" value={range.from} onChange={e => setRange(r => ({ ...r, from: e.target.value }))}
            style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13 }} />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Enddatum</div>
          <input type="date" value={range.to} onChange={e => setRange(r => ({ ...r, to: e.target.value }))}
            style={{ padding: '9px 12px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13 }} />
        </div>
        {search && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>Suche</div>
            <button
              onClick={() => setSearch('')}
              title="Suchbegriff entfernen"
              aria-label={`Suchbegriff „${search}“ entfernen`}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 12px', borderRadius: 8, border: 'none', background: '#f3f4f6', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              „{search}“ <X size={13} />
            </button>
          </div>
        )}
        <div style={{ marginLeft: 'auto', fontSize: 13, color: '#4b5563', paddingBottom: 9 }}>{entries.length} Einträge</div>
      </div>

      {/* Tabelle: Kette Eingang → Annahme → Beginn → Ende */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
              {cols.map(c => (
                <th key={c} style={{ padding: '12px 14px', fontSize: 12, color: '#4b5563', fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap' }}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <SkeletonTableRows rows={6} cols={cols.length} />}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={cols.length} style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Keine Einträge im gewählten Zeitraum</td></tr>
            )}
            {!loading && entries.map(e => (
              <tr key={e.ride_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtTs(e.received_at)}</td>
                <td style={{ padding: '11px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>{fmtTs(e.accepted_at)}</td>
                <td style={{ padding: '11px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>{fmtTs(e.started_at)}</td>
                <td style={{ padding: '11px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>{fmtTs(e.completed_at)}</td>
                <td style={{ padding: '11px 14px', fontSize: 13, maxWidth: 320 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.pickup_address || '—'}</div>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#4b5563' }}>→ {e.dropoff_address || '—'}</div>
                </td>
                <td style={{ padding: '11px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>{e.driver_name || '—'}</td>
                <td style={{ padding: '11px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                  {[e.vehicle_brand, e.vehicle_model].filter(Boolean).join(' ') || '—'}
                  {e.license_plate && <div style={{ color: '#4b5563', fontSize: 12 }}>{e.license_plate}</div>}
                </td>
                <td style={{ padding: '11px 14px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', color: STATUS_COLOR[e.status] || '#374151' }}>
                  {STATUS_DE[e.status] || e.status}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
